

import { GeneralSettings } from "@/context/SettingsContext";

interface GeneralSettingsSectionProps {
  data: GeneralSettings;
  onChange: (data: GeneralSettings) => void;
  onSave: (data: GeneralSettings) => void;
  saving?: boolean;
  readOnly?: boolean;
}

export default function GeneralSettingsSection({
  data,
  onChange,
  onSave,
  saving = false,
  readOnly = false,
}: GeneralSettingsSectionProps) {
  return (
    <div className="bg-white rounded-xl border border-stone-100 shadow-sm p-4">
      <h2 className="text-base font-bold text-stone-800 mb-4 font-serif">General Settings</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Language</label>
          <select
            value={data.language}
            onChange={(e) => onChange({ ...data, language: e.target.value })}
            disabled={readOnly}
            className="w-full px-2.5 py-2 rounded-lg border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none disabled:opacity-50"
          >
            <option value="en">English</option>
            <option value="hi">Hindi</option>
            <option value="mr">Marathi</option>
            <option value="gu">Gujarati</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Timezone</label>
          <select
            value={data.timezone}
            onChange={(e) => onChange({ ...data, timezone: e.target.value })}
            disabled={readOnly}
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none disabled:opacity-50"
          >
            <option value="Asia/Kolkata">India (IST)</option>
            <option value="Asia/Dubai">Dubai</option>
            <option value="Asia/Singapore">Singapore</option>
            <option value="UTC">UTC</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Date Format</label>
          <select
            value={data.dateFormat}
            onChange={(e) => onChange({ ...data, dateFormat: e.target.value })}
            disabled={readOnly}
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none disabled:opacity-50"
          >
            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Currency</label>
          <select
            value={data.currency}
            onChange={(e) => onChange({ ...data, currency: e.target.value })}
            disabled={readOnly}
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none disabled:opacity-50"
          >
            <option value="INR">Indian Rupee (₹)</option>
            <option value="USD">US Dollar ($)</option>
            <option value="EUR">Euro (€)</option>
            <option value="GBP">British Pound (£)</option>
            <option value="AED">UAE Dirham (د.إ)</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Default Tax Rate (%)</label>
          <input
            type="number"
            value={data.taxRate}
            onChange={(e) => onChange({ ...data, taxRate: e.target.value })}
            disabled={readOnly}
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none disabled:opacity-50"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Quotation Validity (Days)</label>
          <input
            type="number"
            min="1"
            max="365"
            value={data.quotationValidityDays}
            onChange={(e) => onChange({ ...data, quotationValidityDays: e.target.value })}
            disabled={readOnly}
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none disabled:opacity-50"
          />
          <p className="text-xs text-stone-400 mt-1">Default days for quotation validity from creation date</p>
        </div>
      </div>

      {/* Testing Mode Section */}
      <div className="mt-6 pt-5 border-t border-stone-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold text-stone-800">Testing Mode</h3>
            <p className="text-xs text-stone-500 mt-0.5">Enable quick login for testing and demos</p>
          </div>
          <button
            onClick={() => onChange({ ...data, testingMode: !data.testingMode })}
            disabled={readOnly}
            className={`relative w-11 h-6 rounded-full transition-colors ${
              data.testingMode ? 'bg-amber-500' : 'bg-stone-300'
            } ${readOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
              data.testingMode ? 'translate-x-5' : 'translate-x-0'
            }`} />
          </button>
        </div>

        {data.testingMode && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <div>
              <label className="block text-xs font-medium text-stone-700 mb-1.5">Quick Login Email</label>
              <input
                type="email"
                value={data.quickLoginEmail}
                onChange={(e) => onChange({ ...data, quickLoginEmail: e.target.value })}
                disabled={readOnly}
                placeholder="admin@shalimar.com"
                className="w-full px-3 py-2.5 rounded-xl border border-amber-200 text-sm bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-700 mb-1.5">Quick Login Password</label>
              <input
                type="password"
                value={data.quickLoginPassword}
                onChange={(e) => onChange({ ...data, quickLoginPassword: e.target.value })}
                disabled={readOnly}
                placeholder="••••••••"
                className="w-full px-3 py-2.5 rounded-xl border border-amber-200 text-sm bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none disabled:opacity-50"
              />
            </div>
            <div className="md:col-span-2">
              <p className="text-xs text-amber-700">
                ⚠️ When enabled, a "Quick Login" button will appear on the login page for fast access during testing.
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 pt-3 border-t border-stone-100">
        <button
          onClick={() => onSave(data)}
          disabled={saving || readOnly}
          className="px-5 py-2 rounded-lg text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {saving ? "Saving..." : readOnly ? "View Only" : "Save General Settings"}
        </button>
      </div>
    </div>
  );
}
